import { supabase } from '../supabaseClient';
import { PriceBreakdown } from './pricing';
import { hoyEc } from './fechas';

export interface PagoPluxAuthorizeResponse {
  status: string;
  token: string;
  id_transaccion?: string;
  [key: string]: any;
}

// Construye el objeto `data` que espera el SDK de PagoPlux (Botón de Pagos),
// incluyendo el callback `onAuthorize` como propiedad del objeto (no como
// función global) según la guía oficial v1.2. Los datos del tarjetahabiente
// (teléfono, identificación) se obtienen del perfil/empresa del usuario logueado.
export async function buildPagoPluxData(
  breakdown: PriceBreakdown,
  userId: string,
  planName: string,
  onAuthorize: (response: PagoPluxAuthorizeResponse) => void
): Promise<Record<string, any>> {
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('full_name, email, phone, company_id')
    .eq('id', userId)
    .single();

  if (profileError || !profile) {
    throw profileError ?? new Error('No se encontró el perfil del usuario');
  }

  const { data: company, error: companyError } = await supabase
    .from('companies')
    .select('ruc, direccion')
    .eq('id', profile.company_id)
    .single();

  if (companyError || !company) {
    throw companyError ?? new Error('No se encontró la empresa del usuario');
  }

  const isRecurrent = breakdown.billingCycle === 'mensual';

  const data: Record<string, any> = {
    PayboxRemail: 'jcifuentes@storsafe.app',
    PayboxSendmail: profile.email,
    PayboxRename: 'ANDEANSOFT S.A.S.',
    PayboxSendname: profile.full_name,
    PayboxBase0: '0.00',
    PayboxBase12: breakdown.totalForCharge.toFixed(2),
    PayboxDescription: `Suscripción StorSafe - Plan ${planName} (${breakdown.billingCycle})`,
    PayboxExtras: profile.company_id,
    PayboxProduction: process.env.NODE_ENV === 'production',
    PayboxEnvironment: process.env.NODE_ENV === 'production' ? 'prod' : 'sandbox',
    PayboxLanguage: 'es',
    PayboxPagoPlux: true,
    PayboxDirection: company.direccion || 'Quito, Ecuador',
    PayBoxClientPhone: profile.phone ?? '',
    PayboxTypeIdentification: 'CC',
    PayBoxClientIdentification:
      process.env.NODE_ENV === 'production'
        ? (company.ruc?.slice(0, 10) ?? '')
        : '1002403234',
    onAuthorize,
  };

  // ⚠️  TEMPORAL - prueba área operativa PagoPlux - REVERTIR después de validar
  if (profile.company_id === 'c2e02690-d032-45be-8bdf-31371672a238' ||
      profile.company_id === 'c6c1d670-4d96-4216-8d50-21424010e0a3') {
    data.PayboxBase0 = '0.00';
    data.PayboxBase12 = '1.00';
  }

  if (isRecurrent) {
    data.PayboxRecurrent = true;
    data.PayboxPermitirCalendarizar = true;
    data.PayboxPagoInmediato = true;
    data.PayboxCobroPrueba = false;
    data.PayboxAmountVariablePlan = true;
    data.PayboxFrequencyPlan = breakdown.billingCycle === 'anual' ? 'ANU' : 'MEN';
    data.PayboxTieneIvaPlan = true;
    data.PayboxDescriptionPlan = `Plan ${planName} ${breakdown.billingCycle === 'anual' ? 'Anual' : 'Mensual'} - StorSafe`;
    data.PayboxPermitirBloquearDiferimientos = true;
  }

  return data;
}

// Se ejecuta cuando el SDK de PagoPlux confirma del lado del cliente que el pago
// fue autorizado (response.status === 'succeeded'). A diferencia del webhook
// (que usa state === 'PAGADO'), este callback ya es una confirmación de pago,
// así que el registro se crea directamente como 'completed'.
export async function handlePagoPluxAuthorize(
  response: PagoPluxAuthorizeResponse,
  companyId: string,
  breakdown: PriceBreakdown
): Promise<void> {
  if (response.status !== 'succeeded') {
    return;
  }

  const { error } = await supabase.from('payments').insert({
    company_id: companyId,
    amount: breakdown.totalForCharge,
    payment_date: hoyEc(),
    method: 'pagoplux',
    status: 'completed',
    token: response.token,
    id_transaccion: response.id_transaccion,
    is_recurring: breakdown.billingCycle === 'mensual',
  });

  if (error) {
    throw error;
  }

  window.location.href = '/pago-procesando';
}
